import { inject, Injectable } from '@angular/core';
import { Store } from '@ngrx/store';

import { LocalStorageService, StorageKey, syncSelect } from '@blaze-shared/platform-data-access';
import { PageContext } from '@blaze-shared/projects-domain';

import * as runtimeSelectors from '../../runtime/runtime.selectors';

const TWO_HOURS_IN_MS = 1000 * 2 * 60 * 60;

/**
 * Type of the Page Context data stored in the Session Storage.
 * Key is the Page Context ID.
 */
type PageContextInSessionStorage = Record<string, PageContext>;

@Injectable({ providedIn: 'root' })
export class PageContextSessionStorageService {
  private readonly store = inject(Store);
  private readonly localStorageService = inject(LocalStorageService);

  private lastValidModifiedTime = 0;

  getAllFromSessionStorage(projectId: string): Record<string, PageContext> {
    const pageContextFromSessionStorage =
      this.localStorageService.getForProject<PageContextInSessionStorage>(
        projectId,
        StorageKey.PageContext,
        window.sessionStorage,
      );

    return removeOldProjectContexts(pageContextFromSessionStorage ?? {});
  }
  addPageContext(pageContext: PageContext): void {
    const projectId = syncSelect(this.store, runtimeSelectors.selectProjectId);
    if (!projectId) {
      console.warn('Page Context - Project ID is not set - cannot add to session storage');
      return;
    }
    const currentContextRecord = this.getAllFromSessionStorage(projectId);
    currentContextRecord[pageContext.id] = pageContext;
    this.saveToSessionStorage(currentContextRecord);
  }

  removePageContext(pageContextId: string): void {
    const projectId = syncSelect(this.store, runtimeSelectors.selectProjectId);
    if (!projectId) {
      console.warn('Page Context - Project ID is not set - cannot remove from session storage');
      return;
    }
    const currentContextRecord = this.getAllFromSessionStorage(projectId);
    delete currentContextRecord[pageContextId];
    this.saveToSessionStorage(currentContextRecord);
  }

  private saveToSessionStorage(pageContextsFromState: Record<string, PageContext>): void {
    const projectId = syncSelect(this.store, runtimeSelectors.selectProjectId);
    if (!projectId) {
      console.warn('Page Context - Project ID is not set - cannot update session storage');
      return;
    }

    // Merge with existing in session storage (in case some were added in other tabs)
    const pageContextsFromSessionStorage = this.getAllFromSessionStorage(projectId);

    // Remove old page contexts from state
    // Page Context from above call is already sanitized
    const sanitizedPageContextsFromState = removeOldProjectContexts(
      pageContextsFromState,
      this.lastValidModifiedTime,
    );

    // For conflicting keys, the state will take precedence
    const mergedPageContexts = {
      ...pageContextsFromSessionStorage,
      ...sanitizedPageContextsFromState,
    };

    this.localStorageService.setForProjectWithCleanup<PageContextInSessionStorage>(
      projectId,
      StorageKey.PageContext,
      mergedPageContexts,
      (currentContexts) => {
        const oldestPageContextKey = Object.keys(currentContexts).reduce((previous, current) =>
          currentContexts[previous].modifiedTime < currentContexts[current].modifiedTime
            ? previous
            : current,
        );

        this.lastValidModifiedTime = currentContexts[oldestPageContextKey].modifiedTime;
        delete currentContexts[oldestPageContextKey];

        return currentContexts;
      },
      window.sessionStorage,
      500,
    );
  }
}

/**
 * Removes page contexts that are older than the specific threshold time
 * @param pageContexts - The page contexts to filter.
 * @param thresholdTime - The cutoff timestamp (in ms since the UNIX epoch); only contexts with a `modifiedTime` greater than this will be kept. Defaults to 2 hours
 * @returns The filtered page contexts.
 */
function removeOldProjectContexts(
  pageContexts: Record<string, PageContext>,
  thresholdTime = Date.now() - TWO_HOURS_IN_MS,
): Record<string, PageContext> {
  const sanitizedPageContexts: Record<string, PageContext> = {};

  for (const pageContext of Object.values(pageContexts)) {
    // If the page context is newer than the threshold time, keep it
    if (pageContext.modifiedTime > thresholdTime) {
      sanitizedPageContexts[pageContext.id] = pageContext;
    }
  }

  return sanitizedPageContexts;
}
